import React from 'react';
import { useFormik } from 'formik';
import { RegistrationStatus, VERIFICATION_CODE_LENGTH } from '@/contracts';
import { registration as actions } from '@/actions';
import { Navigate } from 'react-router-dom';
import { RootState, useAppSelector } from '@/state/store';

import { Button, Container } from '@/components';
import { TextField } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import t from '@/utils/translate';

type Props = {
  redirectUrl: string;
};

export default ({ redirectUrl }: Props) => {
  const { status, withPhone } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.registration.status,
        (state: RootState) => state.registration.withPhone,
      ],
      (status, withPhone) => ({ status, withPhone })
    )
  );

  const formik = useFormik({
    initialValues: { code: '' },
    validate: ({ code }) => {
      const errors: Record<string, string> = {};

      if (!code) {
        errors.code = t('confirmation.phone.hint.code');
      } else if (code.length < VERIFICATION_CODE_LENGTH) {
        errors.code = t('verification.error.codeLength', { length: VERIFICATION_CODE_LENGTH });
      }

      return errors;
    },
    onSubmit: async ({ code }) => {
      await actions.confirmByPhone(code);
      actions.resetRegistration();
    }
  });

  if (
    !withPhone || (status != RegistrationStatus.CONFIRMATION)
  ) {
    return <Navigate to={redirectUrl} replace/>;
  }

  return (
    <>
      <h3>{t('registration.hint.code', true)}</h3>
      <form onSubmit={formik.handleSubmit}>
        <Container margin-v='small'>
          <TextField
            {...formik.getFieldProps('code')}
            required
            label={t('registration.label.code')}
            error={formik.touched.code && !!formik.errors.code}
            helperText={formik.touched.code && formik.errors.code}
            fullWidth
            margin="dense"
            variant="outlined"
            size="small"
            autoFocus
          />
        </Container>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          color="primary"
          size="large"
        >
          {t('action.send')}
        </Button>
      </form>
    </>
  );
};
